import { Children, isValidElement, type ReactNode } from "react";
import { Button, Dialog, Flex, Text, Spinner } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";
import { useManagerContext } from "./context";

type FormDialogProps<T> = {
  title: string | ((isEdit: boolean) => string);
  description?: string | ((isEdit: boolean) => string);
  onSubmit: (data: Record<string, unknown>, item: T | null) => Promise<void>;
  validate?: (data: Record<string, unknown>) => boolean;
  children: ReactNode;
};

function filterChildren(children: ReactNode, isEditMode: boolean): ReactNode[] {
  return Children.toArray(children).filter((child) => {
    if (
      isValidElement<{ hideInEditMode?: boolean }>(child) &&
      child.props.hideInEditMode &&
      isEditMode
    ) {
      return false;
    }
    return true;
  });
}

export function FormDialog<T>({
  title,
  description,
  onSubmit,
  validate,
  children,
}: FormDialogProps<T>) {
  const { t } = useTranslation();
  const {
    dialogOpen,
    closeDialog,
    isEditMode,
    submitting,
    setSubmitting,
    error,
    setError,
    formValues,
    selectedItem,
    refresh,
  } = useManagerContext<T>();

  const filteredChildren = filterChildren(children, isEditMode);

  const resolvedTitle = typeof title === "function" ? title(isEditMode) : title;
  const resolvedDescription =
    typeof description === "function" ? description(isEditMode) : description;

  const isValid = validate ? validate(formValues) : true;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(formValues, selectedItem);
      closeDialog();
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog.Root
      open={dialogOpen}
      onOpenChange={(open) => !open && closeDialog()}
    >
      <Dialog.Content maxWidth="500px">
        <Dialog.Title>{resolvedTitle}</Dialog.Title>
        {resolvedDescription && (
          <Dialog.Description size="2" mb="4">
            {resolvedDescription}
          </Dialog.Description>
        )}

        <Flex direction="column" gap="4">
          {filteredChildren}
        </Flex>

        {error && (
          <Text size="2" color="red" mt="2">
            {error}
          </Text>
        )}

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray" disabled={submitting}>
              {t("common.cancel")}
            </Button>
          </Dialog.Close>
          <Button onClick={handleSubmit} disabled={!isValid || submitting}>
            {submitting && <Spinner size="1" />}
            {submitting
              ? isEditMode
                ? t("common.saving")
                : t("common.creating")
              : isEditMode
                ? t("common.save")
                : t("common.create")}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
